import React, { useId } from "react";

import BucketCircleIcon from "@components/icons/BucketCircleIcon";
import ListIcon from "@components/icons/ListIcon";
import UserCircleIcon from "@components/icons/UserCircleIcon";
import WaterCircleIcon from "@components/icons/WaterCircleIcon";
import Card from "@components/layout/Card";
import { formatVolume } from "@utils/formatVolume";

type Split = {
  /** Litres. */
  volume: number;
  /** The share of the scheme's volume, already formatted by the page. */
  percent: string;
};

type SchemeSummaryCardProps = {
  title: string;
  labels: {
    volume: string;
    subscribers: string;
    used: string;
    remaining: string;
  };
  /** Litres. */
  volume: number;
  subscriberCount: number;
  used: Split;
  remaining: Split;
  /** Shown in place of the used and remaining rows, which belong to an accounting period; absent while one is current. */
  periodNotice?: string;
  /** Marks the card up as a region named by its title, which is a heading. Polestar's dashboard leaves it off. */
  landmark?: boolean;
  className?: string;
};

/**
 * A scheme's volume, subscribers, and the volume used and left. Polestar's scheme
 * dashboard and Zenith's both show it; each page gives its own figures, wording and
 * percentage rule.
 */
const SchemeSummaryCard: React.FunctionComponent<SchemeSummaryCardProps> = ({
  title,
  labels,
  volume,
  subscriberCount,
  used,
  remaining,
  periodNotice,
  landmark = false,
  className,
}) => {
  const id = useId();
  const titleId = landmark ? id : undefined;
  const Title = landmark ? "h2" : "p";

  return (
    <Card
      role={landmark ? "region" : undefined}
      aria-labelledby={titleId}
      className={className}
      header={
        <Title id={titleId} className="flex gap-3">
          <ListIcon /> {title}
        </Title>
      }
    >
      <div className="flex flex-col gap-1">
        <div className="flex border-t py-3 w-full">
          <WaterCircleIcon />
          <section className="pl-2">
            <h4 className="text-gray-500 text-sm">{labels.volume}</h4>
            <p className="text-xl">{formatVolume(volume)}</p>
          </section>
        </div>
        <div className="flex border-t py-3 w-full">
          <UserCircleIcon />
          <section className="pl-2">
            <h4 className="text-gray-500 text-sm">{labels.subscribers}</h4>
            <p className="text-xl">{subscriberCount}</p>
          </section>
        </div>
        <div className="flex border-t py-3 w-full">
          <BucketCircleIcon />
          <section className="pl-2 w-full flex flex-col gap-3">
            {periodNotice ? (
              <p className="text-gray-500 text-sm">{periodNotice}</p>
            ) : (
              <>
                <h4 className="text-gray-500 text-sm">{labels.used}</h4>
                <div className="flex flex-col 2xl:flex-row space-y-4 2xl:space-y-0 2xl:space-x-4 text-xl">
                  <p className="w-1/3 2xl:border-r-2">{used.percent}</p>
                  <p className="w-2/3">{formatVolume(used.volume)}</p>
                </div>
                <h4 className="text-gray-500 text-sm">{labels.remaining}</h4>
                <div className="flex flex-col 2xl:flex-row space-y-4 2xl:space-y-0 2xl:space-x-4 text-xl">
                  <p className="w-1/3 2xl:border-r-2">{remaining.percent}</p>
                  <p className="w-2/3">{formatVolume(remaining.volume)}</p>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </Card>
  );
};

export default SchemeSummaryCard;
